import React from 'react';

import { cn } from '@epoch-ui/lib/utils';
import { Button } from '@epoch-ui/components/Button';
import { CoverPhoto } from '@epoch-ui/components/CoverPhoto';
import { Separator } from '@epoch-ui/components/Separator';
import { EditableEvent } from 'types/event.types';
import { getEventDateString } from 'utils/eventUtils';

interface EmailPreviewRootProps extends React.HTMLAttributes<HTMLDivElement> {}

const EmailPreviewRoot: React.FC<EmailPreviewRootProps> = ({ children, className, ...props }) => {
  return (
    <div
      className={cn('tw-rounded-3xl tw-border tw-border-solid tw-border-border tw-p-5', className)}
      {...props}
    >
      {children}
    </div>
  );
};

interface EmailPreviewProps extends React.HTMLAttributes<HTMLDivElement> {
  event: EditableEvent;
  message?: string | React.ReactNode;
  actionLabel?: string;
}

const EventEmailPreview: React.FC<EmailPreviewProps> = ({
  className,
  event,
  message,
  actionLabel = 'View event',
  ...props
}) => {
  return (
    <EmailPreviewRoot className={cn('tw-flex tw-flex-col tw-gap-8', className)} {...props}>
      {/* Cover photo */}
      <CoverPhoto src={event.photoUrl} alt="Event" className="tw-max-h-[250px] tw-max-w-[250px]" />

      {/* Event details */}
      <div>
        <h2 className="tw-mb-2 tw-text-xl tw-font-medium tw-leading-6 tw-text-gray-900">
          {event.title}
        </h2>
        <p className="tw-left-6 tw-m-0 tw-text-base tw-font-normal tw-text-muted-foreground">
          {getEventDateString(event)}
        </p>
        {actionLabel && (
          <Button variant="primary" size="sm" className="tw-mt-[18px] tw-rounded-xl">
            {actionLabel}
          </Button>
        )}
      </div>

      {/* Content */}
      {message && typeof message === 'string' && (
        <div dangerouslySetInnerHTML={{ __html: message }} />
      )}
      {message && typeof message !== 'string' && <div>{message}</div>}

      <Separator />

      {/* Event organizer */}
      <div className="tw-text-sm tw-text-gray-600">
        <p className="tw-mb-0">Event organizer:</p>
        <p className="tw-mb-0 tw-font-medium tw-text-gray-900">
          {event.hostOrganization?.name || ''}
        </p>
        <p className="tw-mb-0 tw-text-blue-500">
          <a href={`mailto:${event.contactEmail}`} className="tw-underline">
            {event.contactEmail}
          </a>
        </p>
      </div>

      <Separator />
    </EmailPreviewRoot>
  );
};

export { EventEmailPreview };
